import { IGizmo, IGizmoOptions, IGizmoQuestionV2 } from '../../types';

interface IParams {
  config: IGizmoQuestionV2 | IGizmo;
  questions?: IGizmoQuestionV2[];
}

/**
 * Transform a gizmo configuration from the legacy version to the new version
 *
 * @param {IGizmoQuestionV2 | IGizmo} config v2 or v3 question config
 * @param {IGizmoQuestionV2[]} questions optional parameter with the list of all the v2 questions
 *  in the RootGizmo, this list is used when transforming the 'jump' to section. It's utility is
 *  because when creating the Flux expression we want to know the question type to put the
 *  comparisson type correctly.
 *
 * @returns {IGizmo} v3 configuration
 */
function configV2ToV3({ config, questions }: IParams): IGizmo {
  if ((config as IGizmo).fid) {
    config = config as IGizmo;

    if (config.questions) {
      config.gizmos = config.questions.map(q => configV2ToV3({ config: q }));

      delete config.questions;
    }

    return config as IGizmo;
  }
  config = config as IGizmoQuestionV2;

  const gizmo: IGizmo = {
    fid: 'invalidFid',
    type: 'legacy_invalid',
    label: 'Legacy gizmo',
  };

  gizmo.fid = config.f_id;
  gizmo.label = config.title;

  if (config.uid) {
    gizmo.uid = config.uid;
  }
  if (config.number) {
    gizmo.number = config.number;
  }

  if (typeof config.options?.editable === 'boolean') {
    const editable: any = config.options.editable;
    config.options.editable = {
      isEditable: editable,
    };
  }
  if (typeof config.options?.display === 'boolean') {
    const display: any = config.options.display;
    config.options.display = {
      isDisplayed: display,
    };
  }

  const options: IGizmoOptions = {
    ...config.options,
  };

  if (config.column !== undefined && options.indexColumn === undefined) {
    options.indexColumn = {
      isDisplayed: config.column,
    };
  }
  if (config.helpMessage !== undefined) {
    options.help = {
      message: config.helpMessage,
      type: 'tooltip',
    };
  }

  if (config.type === 'text') {
    gizmo.type = 'text_field';

    if (
      config.regex ||
      config.regexexplain ||
      config.minimum ||
      config.maximum ||
      config.uppercaseAnswer !== undefined ||
      config.formatAnswer
    ) {
      if (!options.textField) {
        options.textField = {};
      }

      if (config.regex) {
        options.textField['regex'] = {
          type: 'custom',
          customRegex: config.regex,
          errorMessage: config.regexexplain,
        };
      }

      if (config.minimum) {
        options.textField.minLength = config.minimum;
      }
      if (config.maximum) {
        options.textField.maxLength = config.maximum;
      }
      if (config.uppercaseAnswer !== undefined) {
        options.textField.toUppercase = config.uppercaseAnswer;
      }
      if (config.formatAnswer === 'multiple_line') {
        options.textField.numberOfLines = 3;
      }
    }
  } else if (config.type === 'number') {
    gizmo.type = 'number_field';

    if (
      config.maximum !== undefined ||
      config.minimum !== undefined ||
      config.decimals !== undefined ||
      config.numberOfDecimals !== undefined ||
      config.thousands !== undefined ||
      config.prefix !== undefined
    ) {
      options.numberField = {};

      if (config.maximum !== undefined) {
        options.numberField.maximum = config.maximum;
      }
      if (config.minimum !== undefined) {
        options.numberField.minimum = config.minimum;
      }
      if (config.decimals !== undefined) {
        options.numberField.decimals = config.decimals;
      }
      if (config.numberOfDecimals !== undefined) {
        options.numberField.numberOfDecimals = config.numberOfDecimals;
      }
      if (config.thousands !== undefined) {
        options.numberField.thousands = config.thousands;
      }
      if (config.prefix !== undefined) {
        options.numberField.prefix = config.prefix;
      }
    }
  } else if (config.type === 'folio') {
    gizmo.type = 'folio';
  } else if (config.type === 'title') {
    gizmo.type = 'text';

    if (config.title) {
      gizmo.label = config.title;
    }
  } else if (config.type === 'intro') {
    gizmo.type = 'text';

    if (config.introduction) {
      gizmo.label = config.introduction;
    }
  } else if (config.type === 'subtitle') {
    gizmo.type = 'text';

    if (config.subtitle) {
      gizmo.label = config.subtitle;
    }
  } else if (config.type === 'email') {
    gizmo.type = 'text_field';

    if (!options.textField) {
      options.textField = {};
    }

    options.textField.regex = {
      type: 'email',
    };
  } else if (config.type === 'date') {
    gizmo.type = 'date_field';

    if (config.mode) {
      if (!options.dateField) {
        options.dateField = {};
      }

      options.dateField.mode = config.mode;
    }
  } else if (config.type === 'options') {
    gizmo.type = 'select_field';

    if (config.catalog) {
      options.selectField = {
        ...options.selectField,
        catalogFid: config.catalog,
        gizmoUid: config.options?.selectField?.gizmoUid,
      };

      // if (config.options?.selectField?.value !== undefined) {
      //   options.selectField.value = config.options.selectField.value;
      // }
      // if (config.options?.selectField?.label !== undefined) {
      //   options.selectField.label = config.options.selectField.label;
      // }
      // if (config.options?.selectField?.filter !== undefined) {
      //   options.selectField.filter = config.options.selectField.filter;
      // }
      if (config.other !== undefined) {
        options.selectField.other = config.other;
      }
      if (config.other_message !== undefined) {
        options.selectField.otherMessage = config.other_message;
      }
      if (config.random_order !== undefined) {
        options.selectField.randomOrder = config.random_order;
      }
      if (config.control === 'RADIOBUTTON') {
        options.selectField.controlType = 'radio';
      }
      if (config.metadata !== undefined && Array.isArray(config.metadata)) {
        options.selectField.metadata = config.metadata.map(q =>
          configV2ToV3({ config: q })
        );
      }
    }
  } else if (config.type === 'options_dependant') {
    gizmo.type = 'select_field';

    if (config.catalog && config.fk) {
      options.selectField = {
        ...options.selectField,
        catalogFid: config.catalog,
        parentFieldFid: config.fk,

        other: config.other,
        otherMessage: config.other_message,
        randomOrder: config.random_order,
      };

      if (config.metadata !== undefined && Array.isArray(config.metadata)) {
        options.selectField.metadata = config.metadata.map(q =>
          configV2ToV3({ config: q })
        );
      }
    }
  } else if (config.type === 'multipleOption') {
    gizmo.type = 'select_field';

    if (config.catalog) {
      options.selectField = {
        ...options.selectField,
        catalogFid: config.catalog,
        isMultiple: true,

        other: config.other,
        otherMessage: config.other_message,
        randomOrder: config.random_order,
      };

      if (config.maximum) {
        options.selectField.maximum = config.maximum;
      }
      if (config.minimum) {
        options.selectField.minimum = config.minimum;
      }
    }
  } else if (config.type === 'form_embedded') {
    gizmo.type = 'annex_form';

    if (config.form_id) {
      options.annexForm = {
        ...config.options?.annexForm,
        rootGizmoFid: config.form_id,
        columns: config.columns,
      };
    }
  } else if (config.type === 'jump') {
    gizmo.type = 'section';

    if (config.destinations) {
      gizmo.ops = {
        section: {
          jumps: [],
        },
      };

      for (const destination of config.destinations) {
        const conditional = destination.conditional
          .replace('(', '')
          .replace(')', '');
        let chunks: string[] = [];

        if (conditional.includes('OR')) {
          chunks = conditional.split(' OR ');
        } else if (conditional.includes('AND')) {
          chunks = conditional.split(' AND ');
        } else {
          chunks = [conditional];
        }

        const fluxChunks: string[] = [];
        for (const chunk of chunks) {
          const values = chunk.split(' === ');
          const isStatus = values[0].match(/s\d+/g);
          const isFid = values[0].match(/q\d+/g);
          const isUser = values[0] == 'userId';

          if (isStatus) {
            const fluxChunk = `{{status}} == "${values[0]}"`;
            fluxChunks.push(fluxChunk);
          } else if (isFid) {
            const question = questions?.find(q => q.f_id === values[0]);
            let fluxChunk = `{{${values[0]}}} == `;

            if (
              (question?.type === 'number' || question?.type === 'rate') &&
              !isNaN(Number(values[1]))
            ) {
              fluxChunk += `${values[1]}`;
            } else {
              fluxChunk += `"${values[1]}"`;
            }
            fluxChunks.push(fluxChunk);
          } else if (isUser) {
            let fluxChunk: string | undefined;
            if (values[1] === 'self') {
              fluxChunk = `{{userId}} == {{user}}`;
            } else {
              fluxChunk = `{{userId}} == "${values[1]}"`;
            }

            fluxChunks.push(fluxChunk);
          }

          let flux = '';
          if (conditional.includes('OR')) {
            flux = fluxChunks.join(' OR ');
          } else if (conditional.includes('AND')) {
            flux = fluxChunks.join(' AND ');
          } else {
            flux = fluxChunks.join('');
          }

          gizmo.ops.section?.jumps.push({
            destinyGizmoFid: destination.destination,
            // destinyFid: destination.destination,
            flux,
          });
        }
      }
    }

    gizmo.gizmos = [];
  } else if (config.type === 'file') {
    gizmo.type = 'file_field';
  } else if (config.type === 'phone') {
    gizmo.type = 'phone_field';
  } else if (config.type === 'photo') {
    gizmo.type = 'multimedia_field';

    options.multimediaField = {
      defaultCamera: 'front',
      inputSource: 'camera',
      ...options.multimediaField,
    };

    if (config.defaultCamera === 'BACK') {
      options.multimediaField.defaultCamera = 'back';
    } else if (config.defaultCamera === 'FRONT') {
      options.multimediaField.defaultCamera = 'front';
    }

    if (config.inputSource === 'BOTH') {
      options.multimediaField.inputSource = 'both';
    } else if (config.inputSource === 'CAMERA') {
      options.multimediaField.inputSource = 'camera';
    } else if (config.inputSource === 'LOCAL_FILE') {
      options.multimediaField.inputSource = 'local_file';
    }
  } else if (config.type === 'signature') {
    gizmo.type = 'signature_field';
  } else if (config.type === 'rate') {
    gizmo.type = 'rate_field';

    if (config.rateType === 'yesNo') {
      gizmo.type = 'boolean_field';
    } else {
      options.rateField = {
        type: 'stars',
        quantity: 5,
      };

      if (config.rateType === 'face') {
        options.rateField.type = 'face';
      } else if (config.rateType === 'colorFace') {
        options.rateField.type = 'color_face';
      } else if (config.rateType === 'thumbs') {
        options.rateField.type = 'thumbs';
      }

      if (config.howmany && (config.howmany >= 1 || config.howmany <= 5)) {
        options.rateField.quantity = config.howmany;
      }
    }
  } else if (config.type === 'id_ocr') {
    gizmo.type = 'id_ocr_field';

    options.idOcrField = {
      inputSource: 'local_file',
      processingEnabled: true,
      personalId: {},
      ...options.idOcrField,
    };

    let p: any | undefined;
    const r = config.recognizers?.MexicoVoterIdFrontRecognizer;

    if (config.options?.fileField?.serverProcessing?.personalId && r) {
      if (!options.fileField) {
        options.fileField = {
          serverProcessing: {
            type: 'personal_id',
            personalId: {},
          },
        };
      }
      p = options.fileField!.serverProcessing!.personalId;
    } else {
      if (!options.idOcrField) {
        options.idOcrField = {
          personalId: {},
        };
      }
      p = options.idOcrField!.personalId;
    }

    if (r && p) {
      // Assign values from v2 to v3, if v2 exists and v3 doesn't it will be assigned
      const assign = (v2Key: string, v3PartialKey?: string) => {
        if (!v3PartialKey) {
          v3PartialKey = v2Key;
        }

        if (
          r[v2Key] &&
          !p[`${v3PartialKey}GizmoUid`] &&
          !p[`${v3PartialKey}GizmoFid`]
        ) {
          p![`${v3PartialKey}GizmoFid`] = r[v2Key];
        }
      };

      assign('fullDocumentImage');
      assign('faceImage');
      assign('fullDocumentFrontImage');
      assign('fullDocumentBackImage');
      assign('name');
      assign('first_surname', 'lastName');
      assign('second_surname', 'secondLastName');
      assign('gender');
      assign('dateOfBirth');
      assign('address');
      assign('curp');
      assign('electorKey');
      assign('electoralSection');
      assign('street');
      assign('neighborhood');
      assign('zipcode');
      assign('municipality');
      assign('state');
      assign('electoralState');
      assign('cic');
      assign('ocr');
      assign('mrz');
      assign('expiryYear');
      assign('issueYear');
      assign('registrationYear');
      assign('emissionNumber');
    }
  } else if (config.type === 'location') {
    gizmo.type = 'location_field';
  } else if (config.type === 'code_reader') {
    gizmo.type = 'text_field';

    if (!options.textField) {
      options.textField = {};
    }

    if (!options.textField.codeScanner?.enabled) {
      // options.textField.codeScanner?.enabled = true;
      if (options.textField.codeScanner === undefined) {
        options.textField.codeScanner = { enabled: true };
      } else {
        options.textField.codeScanner.enabled = true;
      }
    }
  }

  if (config.required !== undefined) {
    options.required = {
      isRequired: config.required,
    };
  }

  if (Object.keys(options).length > 0) {
    gizmo.ops = {
      ...gizmo.ops,
      ...options,
    };
  }

  return gizmo;
}

export default configV2ToV3;
